import { useMemo, useCallback } from 'react';
import {
    canRegisterBusiness,
    isBloggerBusiness,
    isBusinessAdmin,
    canEditBusinessCover,
    canNotRegisterBusiness,
} from '../../../../../utils/accessService';
import { ymTrackGoToRegistration } from '../../../../../utils/metrika';
import { useTranslation } from '../../../../../utils/useTranslation';
import { IconKebab } from '../../../../icon/IconKebab';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Menu, MenuItem } from '../../../../navigation/Menu';
import { Typography } from '../../../../typography/Typography';
import { USER_AGREEMENT_LINK } from './constants';
import { ProfileMenuProps } from './types';

export const ProfileMenu = ({
    business,
    user,
    removeMenuItem,
    onReport,
    onPersistBusinessCover,
}: ProfileMenuProps) => {
    const t = useTranslation();
    const isBlogger = isBloggerBusiness(business?.type);
    const canRegister = canRegisterBusiness(user?.businessRegistrationStatus);
    const isAdmin = isBusinessAdmin(business?.id, user);

    const onSupportClick = useCallback(() => {
        window.open(
            `${process.env.REACT_APP_BETA_SUPPORT_FORM_REGULAR_LINK}?login=${user?.login}`,
            '_blank',
        );
    }, [user?.login]);
    const onMoveToRegistration = useCallback(() => {
        ymTrackGoToRegistration({
            page: 'Профиль пользователя',
            userLogin: user?.login,
        });
        window.open(`${process.env.REACT_APP_REGISTRATION_LINK}`);
    }, [user?.login]);

    const menuItems = useMemo(() => {
        if (!business) {
            const userItems = [
                <MenuItem
                    onClick={() => window.open(USER_AGREEMENT_LINK, '_blank')}
                    dataTestId={DataTestId.USER_AGREEMENT}
                >
                    <Typography>{t('Пользовательское соглашение')}</Typography>
                </MenuItem>,
                <MenuItem onClick={onSupportClick} dataTestId={DataTestId.SUPPORT}>
                    <Typography>{t('Поддержка')}</Typography>
                </MenuItem>,
            ];

            if (user && !canNotRegisterBusiness(user.businessRegistrationStatus)) {
                userItems.push(
                    <MenuItem
                        onClick={onMoveToRegistration}
                        dataTestId={DataTestId.APPLY_BETA_TEST}
                    >
                        <Typography>{t('Начать публиковать контент')}</Typography>
                    </MenuItem>,
                );
            }

            return userItems;
        }

        const businessItems = [];

        if (canEditBusinessCover(business, user)) {
            businessItems.push(
                <MenuItem onClick={onPersistBusinessCover}>
                    <Typography>{t('Изменить обложку')}</Typography>
                </MenuItem>,
            );
        }

        if (canRegister && !isBlogger) {
            businessItems.push(
                <MenuItem onClick={() => window.open(`${process.env.REACT_APP_REGISTRATION_LINK}`)}>
                    <Typography>{t('Подтвердить права на профиль')}</Typography>
                </MenuItem>,
            );
        }

        if (!isAdmin) {
            businessItems.push(
                <MenuItem onClick={onReport} dataTestId={DataTestId.REPORT_TEXT}>
                    <Typography noWrap color="error">
                        {t('Пожаловаться')}
                    </Typography>
                </MenuItem>,
            );
        }

        if (removeMenuItem) {
            businessItems.push(removeMenuItem);
        }

        return businessItems;
    }, [
        business,
        canRegister,
        isAdmin,
        isBlogger,
        onMoveToRegistration,
        onPersistBusinessCover,
        onReport,
        onSupportClick,
        t,
        user,
        removeMenuItem,
    ]);

    return (
        <Menu
            minHeight={26}
            pt={0.5}
            pr={1}
            trigger={<IconKebab size={16} color="var(--color-primary)" />}
            options={{ placement: 'right-end' }}
            cursor="pointer"
            dataTestId={DataTestId.PROFILE_KEBAB}
        >
            {menuItems}
        </Menu>
    );
};
